import type { SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement>;

/**
 * Tab bar icon set — dot-grid / matrix aesthetic.
 *
 * Picked as Variant D during the visual restyle playground review
 * (2026-04-14). Each icon is composed of filled dots arranged into a
 * recognizable shape, matching the "crypto-native minimal" design
 * language: no strokes, no details, just pure form through dots.
 *
 * - Wallet: 4×3 dot grid with a larger highlighted dot at position (3,1)
 *   suggesting a card-slot / store of value
 * - Miner: 9-dot pyramid/arrow pointing up — mining/building
 * - History: 12 dots arranged in a circle as a clock face, with the
 *   cardinal points (12/3/6/9) rendered larger + a center dot
 * - Settings: 3×3 dot grid with the center dot enlarged — configuration
 *   / adjustment
 *
 * All icons use `currentColor` for fill so the active/inactive colors
 * come from the BottomTabBar's className.
 */

const baseProps: IconProps = {
  width: 20,
  height: 20,
  viewBox: '0 0 24 24',
  fill: 'none',
  'aria-hidden': true,
};

export function WalletIcon(props: IconProps) {
  return (
    <svg {...baseProps} {...props}>
      {[0, 1, 2, 3].map((col) =>
        [0, 1, 2].map((row) => (
          <circle
            key={`${col}-${row}`}
            cx={5 + col * 5}
            cy={7 + row * 5}
            r={row === 1 && col === 3 ? 1.6 : 1.1}
            fill="currentColor"
          />
        )),
      )}
    </svg>
  );
}

export function MinerIcon(props: IconProps) {
  const DOTS = [
    [12, 3],
    [9, 7], [15, 7],
    [6, 11], [12, 11], [18, 11],
    [9, 15], [15, 15],
    [12, 19],
  ] as const;
  return (
    <svg {...baseProps} {...props}>
      {DOTS.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="1.3" fill="currentColor" />
      ))}
    </svg>
  );
}

export function HistoryIcon(props: IconProps) {
  return (
    <svg {...baseProps} {...props}>
      {Array.from({ length: 12 }, (_, i) => {
        const angle = (i / 12) * Math.PI * 2 - Math.PI / 2;
        const r = i % 3 === 0 ? 1.5 : 1.1;
        return (
          <circle
            key={i}
            cx={12 + Math.cos(angle) * 7}
            cy={12 + Math.sin(angle) * 7}
            r={r}
            fill="currentColor"
          />
        );
      })}
      <circle cx="12" cy="12" r="1.2" fill="currentColor" />
    </svg>
  );
}

export function SettingsIcon(props: IconProps) {
  return (
    <svg {...baseProps} {...props}>
      {[0, 1, 2].map((row) =>
        [0, 1, 2].map((col) => (
          <circle
            key={`${row}-${col}`}
            cx={6 + col * 6}
            cy={6 + row * 6}
            r={row === 1 && col === 1 ? 2 : 1.3}
            fill="currentColor"
          />
        )),
      )}
    </svg>
  );
}

/**
 * Send / Receive dot-matrix icons (5×5 pattern from handoff).
 *
 * Send    — arrow UP   (chevron top + stem):  00100 / 01010 / 10001 / 00100 / 00100
 * Receive — arrow DOWN (stem + chevron bot):  00100 / 00100 / 10001 / 01010 / 00100
 *
 * Default 14px to match handoff `leading={<DotGlyph size={14} />}`.
 */
function dotPattern(pattern: string): ReadonlyArray<readonly [number, number]> {
  const rows = pattern.split('/');
  const out: Array<[number, number]> = [];
  for (let y = 0; y < rows.length; y++) {
    const row = rows[y]!;
    for (let x = 0; x < row.length; x++) {
      if (row[x] === '1') out.push([x, y]);
    }
  }
  return out;
}

const SEND_DOTS = dotPattern('00100/01010/10001/00100/00100');
const RECEIVE_DOTS = dotPattern('00100/00100/10001/01010/00100');

interface DotMatrixProps extends IconProps {
  readonly size?: number;
}

function DotMatrix5x5({
  dots,
  size = 14,
  ...rest
}: DotMatrixProps & { readonly dots: ReadonlyArray<readonly [number, number]> }) {
  const cell = size / 5;
  const r = cell * 0.42;
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      fill="none"
      aria-hidden
      {...rest}
    >
      {dots.map(([x, y], i) => (
        <circle
          key={i}
          cx={x * cell + cell / 2}
          cy={y * cell + cell / 2}
          r={r}
          fill="currentColor"
        />
      ))}
    </svg>
  );
}

export function SendIcon({ size = 14, ...rest }: DotMatrixProps) {
  return <DotMatrix5x5 dots={SEND_DOTS} size={size} {...rest} />;
}

export function ReceiveIcon({ size = 14, ...rest }: DotMatrixProps) {
  return <DotMatrix5x5 dots={RECEIVE_DOTS} size={size} {...rest} />;
}
